import { useContext, useEffect, useState } from "react";
// ** Reactstrap
import { Button, Alert } from "reactstrap";
import Card from "../../ui-components/card";
import Proptypes from "prop-types";
// ** Custom Components
import Flex from "@components/ui-components/flex";
import LoadingButton from "@components/ui-components/loading-button";
// ** Configs
import themeConfig from "@configs/theme-config";
import classnames from "classnames";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NotAuthorized from "@src/views/pages/misc/not-authorized";
import { AbilityContext } from "@context/can";
import Breadcrumbs from "@components/ui-components/breadcrumbs";
import { LoaderOverlay } from "@components/spinner";

const FixedActionFooterLayout = props => {
    const {
        children,
        updateLoading = false,
        onSubmit,
        subject = "",
        isDirty,
        cancelTestId = "",
        submitTestId = "fixed-action-footer-layout-submit",
        loading = false,
        isFromListView = false,
        handleCancel,
        disabled = false,
        hasAccess = true,
        navigateUrl = -1,
        action = "",
        title = "",
        desc = "",
        breadCrumbItem = null
    } = props;
    const ability = useContext(AbilityContext);
    // ** State
    const [visible, setVisible] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        if (window) {
            window.addEventListener("scroll", () => {
                if (window.pageYOffset >= themeConfig.layout.showOffset) {
                    setVisible(true);
                } else {
                    setVisible(false);
                }
            });
        }
    }, [visible]);

    useEffect(() => {
        window.scroll(0, 0);
    }, []);
    return (
        <>
            {breadCrumbItem && <Breadcrumbs items={breadCrumbItem} />}
            {hasAccess ? (
                <>
                    <LoaderOverlay
                        active={loading}
                        children={
                            <Card style={{ marginBottom: "90px" }} children={<div>
                                {title ? <b>{t(title)}</b> : null}
                                {desc ? <p className="mb-1">{t(desc)}</p> : null}
                                {children}
                            </div>} />
                        } />
                    {ability.can(action, subject) ? (
                        <Flex justify="end" className="fixed-footer" align="center">
                            {isDirty && (
                                <Alert
                                    color="info"
                                    data-cy="alert-unsaved-changes"
                                    className="w-max d-none d-md-block mb-0">
                                    <span className="fw-bolder">
                                        {" "}
                                        <i className="mdi mdi-alert" />{" "}
                                        {t("You have unsaved changes")}
                                    </span>
                                </Alert>
                            )}
                            {isDirty ? (
                                <Button
                                    outline
                                    color="danger"
                                    disabled={updateLoading || loading}
                                    onClick={() => (isFromListView ? navigate(navigateUrl) : handleCancel())
                                    }
                                    data-cy={cancelTestId || "cancel-btn"}
                                    className="w-max h-100">
                                    {isFromListView ? t("CANCEL") : t("RESET")}
                                </Button>
                            ) : null}
                            <LoadingButton
                                text="SAVE"
                                testId={submitTestId}
                                subject={subject}
                                action={action}
                                className={classnames("w-max h-100", {
                                    "me-2": visible
                                })}
                                disabled={disabled || !isDirty}
                                loading={updateLoading || loading}
                                onClick={onSubmit}
                                icon="content-save-all-outline" />
                        </Flex>
                    ) : null}
                </>
            ) : (
                <NotAuthorized internal showBtn={false} />
            )}
        </>
    );
};

export default FixedActionFooterLayout;
// ** PropTypes
FixedActionFooterLayout.propTypes = {
    children      : Proptypes.element.isRequired,
    title         : Proptypes.string,
    desc          : Proptypes.string,
    subject       : Proptypes.string,
    action        : Proptypes.string,
    breadCrumbItem: Proptypes.any,
    updateLoading : Proptypes.bool,
    onSubmit      : Proptypes.func.isRequired,
    disabled      : Proptypes.bool,
    isDirty       : Proptypes.bool.isRequired,
    cancelTestId  : Proptypes.string,
    submitTestId  : Proptypes.string,
    loading       : Proptypes.bool,
    isFromListView: Proptypes.bool,
    handleCancel  : Proptypes.func,
    hasAccess     : Proptypes.bool,
    navigateUrl   : Proptypes.oneOfType([Proptypes.string, Proptypes.number])
};
